import React from 'react';

interface MembershipInfoProps {
  currentPackage?: string;
  membershipExpiresAt?: string;
  freeAccountStatus?: boolean;
  subscriptionStatus?: string;
}

const MembershipInfo: React.FC<MembershipInfoProps> = ({
  currentPackage,
  membershipExpiresAt,
  freeAccountStatus = true,
  subscriptionStatus
}) => {
  // MembershipInfo Debug data available

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getMembershipType = () => {
    if (currentPackage) {
      switch (currentPackage.toLowerCase()) {
        case 'starter':
          return 'Starter Plan';
        case 'pro':
          return 'Pro Plan';
        case 'business':
          return 'Business Plan';
        case 'starter_yearly':
          return 'Starter Yearly';
        case 'pro_yearly':
          return 'Pro Yearly';
        case 'business_yearly':
          return 'Business Yearly';
        default:
          return currentPackage;
      }
    }
    return 'FREE';
  };

  const getMembershipDescription = () => {
    if (currentPackage && currentPackage.includes('_yearly')) {
      const monthlyAllowance = getMonthlyAllowance(currentPackage);
      return {
        showIcon: true,
        tooltip: `Credit auto refill ${monthlyAllowance.toLocaleString()} every month. Credits are automatically added to your account on the same date each month as your original subscription date.`
      };
    }
    return { showIcon: false, tooltip: '' };
  };

  const getMonthlyAllowance = (packageName: string) => {
    switch (packageName.toLowerCase()) {
      case 'starter_yearly':
        return 1000;
      case 'pro_yearly':
        return 7500;
      case 'business_yearly':
        return 25000;
      default:
        return 0;
    }
  };

  const getMembershipStatus = () => {
    if (freeAccountStatus && !currentPackage) {
      return {
        type: 'free',
        text: 'Free Account',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-200'
      };
    }

    // Check if subscription is cancelled
    if (subscriptionStatus === 'CANCELLED' && currentPackage && membershipExpiresAt) {
      const expiryDate = new Date(membershipExpiresAt);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysUntilExpiry > 0) {
        return {
          type: 'cancelled',
          text: 'Cancelled',
          color: 'text-orange-600',
          bgColor: 'bg-orange-100',
          borderColor: 'border-orange-200'
        };
      } else {
        return {
          type: 'expired',
          text: 'Expired',
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          borderColor: 'border-red-200'
        };
      }
    }

    if (currentPackage && membershipExpiresAt) {
      const expiryDate = new Date(membershipExpiresAt);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysUntilExpiry > 7) {
        return {
          type: 'active',
          text: 'Active',
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-200'
        };
      } else if (daysUntilExpiry > 0) {
        return {
          type: 'expiring',
          text: 'Expiring Soon',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          borderColor: 'border-yellow-200'
        };
      } else {
        return {
          type: 'expired',
          text: 'Expired',
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          borderColor: 'border-red-200'
        };
      }
    }

    return {
      type: 'unknown',
      text: 'Unknown',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-200'
    };
  };

  const status = getMembershipStatus();

  const isFreeAccount = !currentPackage || (freeAccountStatus && !currentPackage);

  return (
    <div className="text-gray-600">
      <p className="text-sm">
        Current package: <span className="font-medium text-gray-900">{getMembershipType()}</span>
        {membershipExpiresAt && (
          <span className={`ml-2 ${status.type === 'expired' ? 'text-red-600 font-medium' : status.type === 'cancelled' ? 'text-orange-600 font-medium' : ''}`}>
            ({status.type === 'expired' ? 'expired at' : status.type === 'cancelled' ? 'cancelled - expires at' : 'expires at'} {formatDate(membershipExpiresAt)})
          </span>
        )}
        {(() => {
          const description = getMembershipDescription();
          if (description.showIcon) {
            return (
              <span 
                className="ml-2 inline-block w-4 h-4 bg-blue-100 text-blue-600 rounded-full text-xs text-center cursor-help hover:bg-blue-200 transition-colors relative group"
                data-tooltip={description.tooltip}
              >
                i
                {/* Custom tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs text-left rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-80">
                  {description.tooltip}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </span>
            );
          }
          return null;
        })()}
      </p>
      
      {/* Show cancelled info */}
      {status.type === 'cancelled' && (
        <p className="mt-1 text-sm text-orange-600">
          ⚠️ Subscription cancelled - benefits continue until expiry date
        </p>
      )}
      
      {/* Show resubscribe link for cancelled membership */}
      {status.type === 'cancelled' && (
        <p className="mt-2">
          <a 
            href="/pricing" 
            className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors"
          >
            Resubscribe to {getMembershipType()}
          </a>
        </p>
      )}
      
      {/* Show renew link for expired membership */}
      {status.type === 'expired' && (
        <p className="mt-2">
          <a 
            href="/pricing" 
            className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
          >
            Renew Membership
          </a>
        </p>
      )}
      
      {/* Show purchase link for free account */}
      {isFreeAccount && (
        <p className="mt-2">
          <a href="/pricing" className="text-blue-600 hover:text-blue-800 underline">Purchase membership</a>
        </p>
      )}

      {/* Show change plan link for active subscriptions only */}
      {currentPackage && !isFreeAccount && subscriptionStatus === 'ACTIVE' && (
        <p className="mt-2">
          <a 
            href="/pricing" 
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Change Plan
          </a>
        </p>
      )}
    </div>
  );
};

export default MembershipInfo;
